import React, { useEffect, useState } from 'react';
import { Send, CheckCircle2, XCircle, Archive, RotateCcw, History, X, Eye } from 'lucide-react';
import { toast } from 'react-toastify';
import useContentGovernanceStore from '@/stores/SuperAdminStore/ContentGovernanceStore';
import { CourseStatus, GovernanceCourse, LifecycleAction } from '@/services/PagesServices/ContentGovernanceService';
import CourseStructureTree from './CourseStructureTree';

const STATUS_TABS: { key: CourseStatus | ''; label: string }[] = [
  { key: '', label: 'All' },
  { key: 'draft', label: 'Draft' },
  { key: 'pending', label: 'In Review' },
  { key: 'published', label: 'Published' },
  { key: 'archived', label: 'Archived' },
];

// Which lifecycle actions are valid from each status.
const ACTIONS_BY_STATUS: Record<CourseStatus, { action: LifecycleAction; label: string; icon: React.ReactNode; variant: string }[]> = {
  draft:     [{ action: 'submit',  label: 'Submit for review', icon: <Send size={14} />, variant: 'primary' }],
  pending:   [
    { action: 'approve', label: 'Approve & Publish', icon: <CheckCircle2 size={14} />, variant: 'success' },
    { action: 'reject',  label: 'Reject', icon: <XCircle size={14} />, variant: 'danger' },
  ],
  published: [{ action: 'archive', label: 'Archive', icon: <Archive size={14} />, variant: 'ghost' }],
  archived:  [{ action: 'restore', label: 'Restore to draft', icon: <RotateCcw size={14} />, variant: 'ghost' }],
};

const ApprovalQueue: React.FC = () => {
  const {
    courses, countsByStatus, loadingCourses, fetchCourses, transition, saving,
    history, fetchHistory,
  } = useContentGovernanceStore();

  const [statusFilter, setStatusFilter] = useState<CourseStatus | ''>('');
  const [drawerCourse, setDrawerCourse] = useState<GovernanceCourse | null>(null);
  const [drawerTab, setDrawerTab] = useState<'structure' | 'history'>('structure');

  useEffect(() => { fetchCourses(statusFilter); }, [statusFilter, fetchCourses]);

  const act = async (course: GovernanceCourse, action: LifecycleAction) => {
    let note: string | undefined;
    if (action === 'reject') {
      const reason = window.prompt('Reason for rejection (optional)') || '';
      note = reason.trim() || undefined;
    }
    const res = await transition(course.id, action, note);
    res.success ? toast.success(res.message) : toast.error(res.message);
  };

  const openDrawer = (course: GovernanceCourse, tab: 'structure' | 'history') => {
    setDrawerCourse(course);
    setDrawerTab(tab);
    if (tab === 'history') fetchHistory(course.id);
  };

  return (
    <div className="s-admin-ca-queue">
      <div className="s-admin-ca-statuschips">
        {STATUS_TABS.map((t) => {
          const count = t.key === '' ? null : countsByStatus?.[t.key] ?? 0;
          return (
            <button
              key={t.key || 'all'}
              className={`s-admin-ca-chip ${statusFilter === t.key ? 'active' : ''}`}
              onClick={() => setStatusFilter(t.key)}
            >
              {t.label}{count !== null && <span className="s-admin-ca-chip-count">{count}</span>}
            </button>
          );
        })}
      </div>

      {loadingCourses ? (
        <div className="s-admin-ca-empty">Loading courses…</div>
      ) : courses.length === 0 ? (
        <div className="s-admin-ca-empty">No courses in this state.</div>
      ) : (
        <table className="s-admin-ca-table">
          <thead>
            <tr>
              <th>Course</th>
              <th>Category</th>
              <th>Creator</th>
              <th>Structure</th>
              <th>Status</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {courses.map((c) => (
              <tr key={c.id}>
                <td className="s-admin-ca-course-title">{c.title}</td>
                <td className="s-admin-ca-muted">{c.categoryName || '—'}</td>
                <td className="s-admin-ca-muted">{c.creatorName || '—'}</td>
                <td className="s-admin-ca-muted">{c.sectionCount} sec · {c.lessonCount} les</td>
                <td><span className={`s-admin-ca-status s-admin-ca-status-${c.status}`}>{c.status}</span></td>
                <td>
                  <div className="s-admin-ca-row-actions">
                    <button className="s-admin-ca-icon" title="View structure" onClick={() => openDrawer(c, 'structure')}><Eye size={15} /></button>
                    <button className="s-admin-ca-icon" title="History" onClick={() => openDrawer(c, 'history')}><History size={15} /></button>
                    {ACTIONS_BY_STATUS[c.status].map((a) => (
                      <button
                        key={a.action}
                        className={`s-admin-ca-btn-${a.variant} s-admin-ca-btn-sm`}
                        disabled={saving}
                        onClick={() => act(c, a.action)}
                      >
                        {a.icon} {a.label}
                      </button>
                    ))}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {drawerCourse && (
        <div className="s-admin-ca-drawer-overlay" onClick={() => setDrawerCourse(null)}>
          <div className="s-admin-ca-drawer" onClick={(e) => e.stopPropagation()}>
            <div className="s-admin-ca-drawer-head">
              <h3>{drawerCourse.title}</h3>
              <button className="s-admin-ca-icon" onClick={() => setDrawerCourse(null)}><X size={18} /></button>
            </div>
            <div className="s-admin-ca-drawer-tabs">
              <button className={drawerTab === 'structure' ? 'active' : ''} onClick={() => { setDrawerTab('structure'); }}>Structure</button>
              <button className={drawerTab === 'history' ? 'active' : ''} onClick={() => { setDrawerTab('history'); fetchHistory(drawerCourse.id); }}>History</button>
            </div>
            <div className="s-admin-ca-drawer-body">
              {drawerTab === 'structure' ? (
                <CourseStructureTree courseId={drawerCourse.id} />
              ) : (
                <ul className="s-admin-ca-history">
                  {history.length === 0 ? (
                    <li className="s-admin-ca-empty">No transitions recorded.</li>
                  ) : history.map((h) => (
                    <li key={h.id} className="s-admin-ca-history-item">
                      <div className="s-admin-ca-history-line">
                        {h.fromStatus && <span className={`s-admin-ca-status s-admin-ca-status-${h.fromStatus}`}>{h.fromStatus}</span>}
                        <span className="s-admin-ca-history-arrow">→</span>
                        <span className={`s-admin-ca-status s-admin-ca-status-${h.toStatus}`}>{h.toStatus}</span>
                      </div>
                      <div className="s-admin-ca-history-meta">
                        {h.actorName || h.actorUuid} · {new Date(h.createdAt).toLocaleString()}
                      </div>
                      {h.note && <div className="s-admin-ca-history-note">“{h.note}”</div>}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ApprovalQueue;
